// MyTransactionsPage - Cardholder view of their own transactions
import React, { useEffect, useState, useCallback } from 'react';
import API from '../lib/api';
import { usePeriod } from '../contexts/PeriodContext';
import { useAuth } from '../contexts/AuthContext';
import { StatusChip } from '../components/app/StatusChip';
import { SuggestionBadge } from '../components/app/SuggestionBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Search, RefreshCw, Send } from 'lucide-react';
import { LinkedFeeBadge } from '../components/app/LinkedFeeBadge';
import { RefundBadge } from '../components/app/RefundBadge';

export default function MyTransactionsPage() {
  const { activePeriod } = usePeriod();
  const { user } = useAuth();
  const [txns, setTxns]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [panelOpen, setPanelOpen]     = useState(false);
  const [categories, setCategories]   = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [cat, setCat]   = useState('');
  const [cc, setCC]     = useState('');
  const [desc, setDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (activePeriod?.id) params.period_id = activePeriod.id;
      if (search) params.search = search;
      const res = await API.get('/transactions', { params });
      setTxns(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch(e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [activePeriod?.id, search]);

  useEffect(() => {
    load();
    API.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    API.get('/cost-centers').then(r => setCostCenters(r.data)).catch(() => {});
  }, [load]);

  const openPanel = (txn) => {
    setSelectedTxn(txn);
    setCat(txn.category_id || '');
    setCC(txn.cost_center_id || '');
    setDesc(txn.description || '');
    setPanelOpen(true);
    API.get(`/transactions/${txn.id}/history`).then(r => setHistory(r.data)).catch(() => {});
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.put(`/transactions/${selectedTxn.id}/allocate`, { category_id: cat || null, cost_center_id: cc || null, description: desc || null });
      toast.success('Saved'); setPanelOpen(false); load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const applySuggestion = () => {
    if (selectedTxn?.suggestion?.category_id) setCat(selectedTxn.suggestion.category_id);
    if (selectedTxn?.suggestion?.cost_center_id) setCC(selectedTxn.suggestion.cost_center_id);
  };

  const doSubmit = async () => {
    try {
      const res = await API.post('/approvals/submit', { period_id: activePeriod?.id });
      toast.success(`Submitted ${res.data.submitted} transactions`);
      setSubmitDialogOpen(false); load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
  };

  const debitTxns = txns.filter(t => !t.is_credit && !t.is_payment);
  const codedCount = debitTxns.filter(t => t.category_id && t.cost_center_id).length;
  const totalAmount = debitTxns.reduce((sum, t) => sum + (t.amount || 0), 0);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">My Transactions</h1>
          <p className="text-sm text-slate-500">{activePeriod ? `Period: ${activePeriod.label}` : 'All periods'}</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} className="h-8 pl-7 text-sm w-44" />
          </div>
          {activePeriod && codedCount > 0 && (
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => setSubmitDialogOpen(true)} data-testid="my-txn-submit-btn">
              <Send size={13} className="mr-1" /> Submit ({codedCount})
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="kpi-card">
          <div className="text-xs text-slate-500">Total Spend</div>
          <div className="text-xl font-bold tabular-nums">{totalAmount.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0 })}</div>
        </div>
        <div className="kpi-card">
          <div className="text-xs text-slate-500">Coded</div>
          <div className="text-xl font-bold">{codedCount} / {debitTxns.length}</div>
        </div>
        <div className="kpi-card">
          <div className="text-xs text-slate-500">Remaining</div>
          <div className="text-xl font-bold text-orange-600">{debitTxns.length - codedCount}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="data-table" data-testid="my-transactions-table">
          <thead><tr>
            <th>Status</th><th>Date</th><th>Merchant</th>
            <th className="text-right">Amount</th>
            <th>Category</th><th>Cost Center</th><th>Suggestion</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8"><RefreshCw size={16} className="animate-spin inline text-slate-400" /></td></tr>
            ) : txns.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">No transactions for this period</td></tr>
            ) : txns.map(t => (
              <tr
                key={t.id}
                className={`cursor-pointer ${['PENDING','NEEDS_REVIEW'].includes(t.status) && !t.is_credit ? 'exception' : ''}`}
                onClick={() => openPanel(t)}
              >
                <td><StatusChip status={t.status} size="xs" /></td>
                <td className="text-xs">{t.date}</td>
                <td className="text-sm font-medium truncate max-w-48" title={t.description_raw}>{t.description_raw}</td>
                <td className={`text-right tabular-nums font-semibold ${(t.is_credit || t.is_refund) ? 'text-emerald-600' : ''}`}>
                  {(t.amount_with_fees || t.amount)?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                </td>
                <td className="text-xs">{t.category_name || <span className="text-orange-400">Not coded</span>}</td>
                <td className="text-xs">{t.cost_center_name || '—'}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    {t.is_refund && <RefundBadge transaction={t} />}
                    {t.suggestion && <SuggestionBadge suggestion={t.suggestion} />}
                    {t.has_linked_fees && <LinkedFeeBadge transaction={t} />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Allocation Sheet */}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent side="right" className="w-[380px] bg-white">
          <SheetHeader><SheetTitle className="text-sm">Code Transaction</SheetTitle></SheetHeader>
          {selectedTxn && (
            <div className="overflow-y-auto space-y-4 pt-4 pb-20">
              <div className="border rounded p-3 space-y-1">
                <div className="text-xs text-slate-500">{selectedTxn.date}</div>
                <div className="font-semibold">{selectedTxn.description_raw}</div>
                <div className="text-xl font-bold tabular-nums">
                  {(selectedTxn.amount_with_fees || selectedTxn.amount)?.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' })}
                  {selectedTxn.has_linked_fees && (
                    <span className="ml-2 text-xs font-normal text-violet-600">
                      (base {selectedTxn.amount?.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' })} + fee)
                    </span>
                  )}
                </div>
                <div className="flex gap-2 pt-1">
                  <StatusChip status={selectedTxn.status} size="xs" />
                  {selectedTxn.is_refund && <RefundBadge transaction={selectedTxn} />}
                  {selectedTxn.suggestion && <SuggestionBadge suggestion={selectedTxn.suggestion} />}
                  {selectedTxn.has_linked_fees && <LinkedFeeBadge transaction={selectedTxn} />}
                </div>
              </div>

              {selectedTxn.suggestion?.source_type !== 'needs_review' && (
                <div className="bg-blue-50 border border-blue-100 rounded p-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-700">Suggestion</span>
                    <button onClick={applySuggestion} className="text-xs text-blue-600 hover:underline font-semibold">Apply</button>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">{selectedTxn.suggestion?.explanation}</p>
                </div>
              )}

              <div>
                <label className="text-xs font-medium">Category *</label>
                <Select value={cat} onValueChange={setCat}>
                  <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-white">{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium">Cost Center *</label>
                <Select value={cc} onValueChange={setCC}>
                  <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-white">{costCenters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium">Notes</label>
                <Textarea value={desc} onChange={e => setDesc(e.target.value)} className="mt-1 text-sm h-16 resize-none" />
              </div>

              {/* Allocation history */}
              {history.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-2">Coding History</div>
                  {history.slice(0, 3).map((h, i) => (
                    <div key={i} className="text-xs border rounded p-2 mb-1">
                      <div className="flex justify-between"><span className="font-medium">v{h.version}</span><span className="text-slate-400">{h.coded_by}</span></div>
                      <div>{h.category_name} / {h.cost_center_name}</div>
                    </div>
                  ))}
                </div>
              )}

              <Button size="sm" className="w-full" onClick={save} disabled={saving || ['APPROVED','SUBMITTED'].includes(selectedTxn?.status)}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Submit dialog */}
      <AlertDialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit for Approval?</AlertDialogTitle>
            <AlertDialogDescription>Submit all coded transactions for period {activePeriod?.label}?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSubmit} className="bg-amber-600">Submit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
